/**
 * Enterprise Wishlist Drawer - Extends BaseDrawer
 *
 * Wishlist-specific implementation for managing product wishlist and drawer.
 * Reuses BaseDrawer for common drawer behavior (focus trap, scroll lock, overlay, etc.)
 *
 * Features:
 * - localStorage persistence for wishlist items
 * - Wishlist button integration across 4 touchpoints:
 *   1. Now Trending Collection
 *   2. Main Product PDP
 *   3. Collections Pages
 *   4. "YOU MIGHT LIKE" Component
 * - Add to cart (single and batch)
 * - Quantity controls
 * - Empty state handling
 * - Login message for non-logged-in users
 *
 * @extends BaseDrawer
 * @version 2.0.0 (Enterprise)
 * @date October 26, 2025
 */

class EnterpriseWishlistDrawer extends BaseDrawer {
  constructor() {
    super();

    // Prevent re-initialization
    if (window.wishlistManagerInitialized) {
      window.debugLogger.warn("EnterpriseWishlistDrawer already initialized. Skipping.");
      return;
    }
    window.wishlistManagerInitialized = true;

    // Initialize wishlist items array
    this.wishlistItems = [];
  }

  connectedCallback() {
    // Call BaseDrawer's connectedCallback to initialize overlay, panel, etc.
    super.connectedCallback();

    // CRITICAL FIX: Wait for child elements to be parsed
    // connectedCallback fires BEFORE children are added to the custom element
    // Use setTimeout to defer initialization until after DOM parsing completes
    setTimeout(() => {
      this.initializeWishlistDrawer();
    }, 0);
  }

  /**
   * Initialize the wishlist drawer after DOM is ready
   */
  initializeWishlistDrawer() {
    // Find the drawer element (try inside first, then document-wide)
    this.drawer = this.querySelector('.material-drawer');

    if (!this.drawer) {
      this.drawer = document.querySelector('.material-drawer');
    }

    if (!this.drawer) {
      window.debugLogger.error('CRITICAL: .material-drawer element not found anywhere in DOM!');
      return; // Exit early if drawer not found
    }

    // Load wishlist items from localStorage
    this.loadWishlist();

    // Clean up any stale or corrupted data
    this.cleanupWishlist();

    // Set up event listeners (wishlist-specific)
    this.setupEventListeners();

    // Update wishlist buttons across the site
    this.updateWishlistButtons();

    // Initialize drawer body with the correct class based on wishlist state
    if (this.drawer) {
      const drawerBody = this.drawer.querySelector('#wishlist-drawer-body');
      if (drawerBody) {
        // Reset to base class
        drawerBody.className = 'material-drawer__body';

        // Initialize drawer content
        this.updateDrawer();
      }
    }
  }

  /**
   * Set up all event listeners
   * CRITICAL: Preserves wishlist button functionality across all 4 touchpoints
   */
  setupEventListeners() {
    // Handle wishlist button clicks with high priority
    // CRITICAL: .iWishAddColl buttons in Now Trending, PDP, Collections, "YOU MIGHT LIKE"
    document.addEventListener('click', (event) => {
      const wishlistButton = event.target.closest('.iWishAddColl');
      if (wishlistButton && wishlistButton.dataset.productId) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.toggleWishlistItem(wishlistButton.dataset);
        return false;
      }

      // Handle wishlist icon click to open drawer (using event delegation)
      const wishlistIcon = event.target.closest('.header__icon--wishlist');
      if (wishlistIcon) {
        event.preventDefault();
        event.stopPropagation();
        this.toggleDrawer(true);
        return false;
      }
    }, true); // Use capture phase to intercept events early

    // NOTE: BaseDrawer handles close button, overlay click, and escape key
    // We only need to handle wishlist-specific interactions here

    // Use event delegation for dynamically created elements
    if (this.drawer) {
      const drawerBody = this.drawer.querySelector('#wishlist-drawer-body');
      if (drawerBody) {
        drawerBody.addEventListener('click', (event) => {
          // IMPORTANT: Stop propagation for all clicks inside drawer
          // This prevents clicks from bubbling up to the outside click handler
          event.stopPropagation();

          // Quantity increase
          if (event.target.classList.contains('quantity-increase')) {
            const input = event.target
              .closest('.material-drawer__quantity')
              .querySelector('.quantity-input');
            if (input) {
              const currentValue = parseInt(input.value, 10) || 1;
              input.value = currentValue + 1;
              this.updateItemQuantity(
                event.target.closest('.material-drawer__item'),
                currentValue + 1
              );
            }
          }

          // Quantity decrease
          else if (event.target.classList.contains('quantity-decrease')) {
            const input = event.target
              .closest('.material-drawer__quantity')
              .querySelector('.quantity-input');
            if (input) {
              const currentValue = parseInt(input.value, 10) || 1;
              if (currentValue > 1) {
                input.value = currentValue - 1;
                this.updateItemQuantity(
                  event.target.closest('.material-drawer__item'),
                  currentValue - 1
                );
              }
            }
          }

          // Remove item
          else if (
            event.target.classList.contains('trash-btn') ||
            event.target.closest('.material-drawer__remove-button')
          ) {
            const item = event.target.closest('.material-drawer__item');
            if (item) {
              this.removeFromWishlist(item);
            }
          }

          // Add to Bag
          else if (event.target.classList.contains('material-drawer__add-to-cart')) {
            const item = event.target.closest('.material-drawer__item');
            if (item) {
              this.addToCart(item);
            }
          }

          // Continue shopping button
          else if (event.target.classList.contains('material-drawer__continue')) {
            this.toggleDrawer(false);
          }
        });

        // Input change event for quantity
        drawerBody.addEventListener('change', (event) => {
          if (event.target.classList.contains('quantity-input')) {
            const item = event.target.closest('.material-drawer__item');
            if (item) {
              let value = parseInt(event.target.value, 10);
              if (isNaN(value) || value < 1) {
                value = 1;
                event.target.value = value;
              }
              this.updateItemQuantity(item, value);
            }
          }
        });
      }
    }

    // NOTE: Escape key and click-outside are handled by BaseDrawer
  }

  /**
   * Toggle drawer open/close (wrapper for backward compatibility)
   * @param {boolean} show - True to open, false to close
   */
  toggleDrawer(show = true) {
    if (show) {
      this.open();
    } else {
      this.close();
    }
  }

  /**
   * Override BaseDrawer's open method to add wishlist-specific logic
   * @param {HTMLElement} triggeredBy - Element that triggered the drawer
   */
  open(triggeredBy) {
    // DON'T call super.open() - it conflicts with wishlist CSS!
    // Just do what the original wishlist.js does

    // CRITICAL: Add .open class to .material-drawer for CSS compatibility
    if (this.drawer) {
      this.drawer.classList.add('open');
      document.body.classList.add('overflow-hidden'); // Lock scroll

      // FORCE visibility to visible with !important (CSS override)
      this.drawer.style.setProperty('visibility', 'visible', 'important');
      this.drawer.style.setProperty('opacity', '1', 'important');
      this.drawer.style.setProperty('display', 'block', 'important');
      this.drawer.style.setProperty('z-index', '999999', 'important');
      this.drawer.style.setProperty('pointer-events', 'auto', 'important');

      // Also force the content and overlay to be visible
      const content = this.drawer.querySelector('.material-drawer__content');
      const overlay = this.drawer.querySelector('.material-drawer__overlay');
      if (content) {
        content.style.setProperty('visibility', 'visible', 'important');
        content.style.setProperty('opacity', '1', 'important');
        content.style.setProperty('transform', 'scaleX(1) translateX(0)', 'important');
        content.style.setProperty('right', '0px', 'important');
        content.style.setProperty('position', 'fixed', 'important');
        content.style.setProperty('top', '0px', 'important');
        content.style.setProperty('bottom', '0px', 'important');
        content.style.setProperty('height', '100vh', 'important');
      }
      if (overlay) {
        overlay.style.setProperty('visibility', 'visible', 'important');
        overlay.style.setProperty('opacity', '1', 'important');
        overlay.style.setProperty('pointer-events', 'auto', 'important');
      }
    }

    // Get the drawer body
    const drawerBody = this.querySelector('#wishlist-drawer-body');
    if (drawerBody) {
      // Clear classes except the base class
      drawerBody.className = 'material-drawer__body';
    }

    // Update drawer content when opening
    this.updateDrawer();

    // Set up drawer events after content is updated
    this.setupDrawerEvents();

    // Set up close handlers (since we're not using super.open())
    this.setupCloseHandlers();
  }

  /**
   * Set up event handlers for closing the drawer
   */
  setupCloseHandlers() {
    // Close button (X button)
    const closeButton = this.drawer.querySelector('.material-drawer__back');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.close());
    }

    // Escape key
    this._escapeHandler = (event) => {
      if (event.key === 'Escape' && this.drawer && this.drawer.classList.contains('open')) {
        this.close();
      }
    };
    document.addEventListener('keydown', this._escapeHandler);

    // Click outside / overlay
    this._outsideClickHandler = (event) => {
      if (!this.drawer || !this.drawer.classList.contains('open')) return;

      const drawerContent = this.drawer.querySelector('.material-drawer__content');
      const overlay = this.drawer.querySelector('.material-drawer__overlay');

      // Close if clicking overlay
      if (overlay && overlay.contains(event.target)) {
        this.close();
        return;
      }

      // Close if clicking outside drawer content
      if (drawerContent && !drawerContent.contains(event.target)) {
        this.close();
      }
    };
    document.addEventListener('click', this._outsideClickHandler);

    // Continue Shopping button (in empty state)
    const continueButton = this.drawer.querySelector('.material-drawer__continue');
    if (continueButton) {
      continueButton.addEventListener('click', () => this.close());
    }
  }

  /**
   * Override BaseDrawer's close method to remove .open class
   */
  close() {
    if (this.drawer) {
      this.drawer.classList.remove('open');
      document.body.classList.remove('overflow-hidden'); // Unlock scroll

      // CRITICAL: Remove all inline styles we set when opening
      // Otherwise !important styles prevent CSS from hiding the drawer
      this.drawer.style.removeProperty('visibility');
      this.drawer.style.removeProperty('opacity');
      this.drawer.style.removeProperty('display');
      this.drawer.style.removeProperty('z-index');
      this.drawer.style.removeProperty('pointer-events');

      const content = this.drawer.querySelector('.material-drawer__content');
      const overlay = this.drawer.querySelector('.material-drawer__overlay');

      if (content) {
        content.style.removeProperty('visibility');
        content.style.removeProperty('opacity');
        content.style.removeProperty('transform');
        content.style.removeProperty('right');
        content.style.removeProperty('position');
        content.style.removeProperty('top');
        content.style.removeProperty('bottom');
        content.style.removeProperty('height');
      }

      if (overlay) {
        overlay.style.removeProperty('visibility');
        overlay.style.removeProperty('opacity');
        overlay.style.removeProperty('pointer-events');
      }
    }

    // Clean up event listeners
    if (this._escapeHandler) {
      document.removeEventListener('keydown', this._escapeHandler);
      this._escapeHandler = null;
    }
    if (this._outsideClickHandler) {
      document.removeEventListener('click', this._outsideClickHandler);
      this._outsideClickHandler = null;
    }
  }

  /**
   * Add single item to Shopify cart
   * @param {HTMLElement} item - Wishlist item element
   */
  addToCart(item) {
    if (!item) return;

    const variantId = item.dataset.variantId;
    const quantity = item.querySelector('.quantity-input')
      ? parseInt(item.querySelector('.quantity-input').value, 10)
      : 1;

    if (!variantId) {
      window.debugLogger.error('No variant ID found for item', item);
      return;
    }

    // Find the button and update its text
    const addToCartButton = item.querySelector('.material-drawer__add-to-cart');
    if (addToCartButton) {
      const originalWidth = addToCartButton.offsetWidth;
      const originalText = addToCartButton.textContent;

      addToCartButton.style.width = originalWidth + 'px';
      addToCartButton.textContent = 'ADDING...';
      addToCartButton.disabled = true;
      addToCartButton.classList.add('loading');
    }

    // Add to cart using Enterprise Cart API (Skill #2)
    const compareAtPriceRaw = item.dataset.compareAtPrice || '';
    const properties = {};
    if (compareAtPriceRaw) {
      properties['_compare_at_price'] = compareAtPriceRaw;
    }

    window.cartAPI.addToCart(variantId, quantity, {
      properties: properties,
      openDrawer: false,
      dispatchEvents: true
    })
      .then((cart) => {
        // Update button to success state with checkmark
        if (addToCartButton) {
          addToCartButton.innerHTML = '<span class="checkmark">✓</span>';
          addToCartButton.classList.remove('loading');
          addToCartButton.classList.add('success');

          // Reset button after 1 second
          setTimeout(() => {
            addToCartButton.textContent = 'Add to Bag';
            addToCartButton.disabled = false;
            addToCartButton.classList.remove('success');
            addToCartButton.style.width = '';
          }, 1000);
        }
      })
      .catch((error) => {
        window.debugLogger.error('Error adding to cart', error);

        // Reset button state on error
        if (addToCartButton) {
          addToCartButton.textContent = 'Add to Bag';
          addToCartButton.disabled = false;
          addToCartButton.classList.remove('loading');
          addToCartButton.style.width = '';
        }
      });
  }

  /**
   * Remove item from wishlist
   * @param {HTMLElement} item - Wishlist item element
   */
  removeFromWishlist(item) {
    if (!item) return;

    const productId = item.dataset.productId;
    if (!productId) {
      window.debugLogger.error('No product ID found for item', item);
      return;
    }

    // Remove from wishlist array
    this.wishlistItems = this.wishlistItems.filter((item) => item.id !== productId);

    // Update localStorage
    localStorage.setItem('wishlist', JSON.stringify(this.wishlistItems));

    // Update buttons across the site
    this.updateWishlistButtons();

    // Get the drawer body
    const drawerBody = this.drawer.querySelector('#wishlist-drawer-body');
    if (drawerBody) {
      drawerBody.className = 'material-drawer__body';
    }

    // Update drawer with proper state
    this.updateDrawer();
  }

  /**
   * Add all wishlist items to cart
   */
  addAllToCart() {
    if (!this.wishlistItems.length) return;

    // Find and update the add all button
    const addAllButton = this.drawer.querySelector('.material-drawer__add-all-to-cart');
    if (addAllButton) {
      const originalWidth = addAllButton.offsetWidth;
      const originalHeight = addAllButton.offsetHeight;

      addAllButton.style.width = originalWidth + 'px';
      addAllButton.style.height = originalHeight + 'px';
      addAllButton.textContent = 'ADDING...';
      addAllButton.disabled = true;
      addAllButton.classList.add('loading');
    }

    const items = this.wishlistItems.map((item) => {
      const properties = item.compare_at_price
        ? { _compare_at_price: item.compare_at_price }
        : {};

      return {
        id: item.variant,
        quantity: item.quantity || 1,
        properties
      };
    });

    // Add all items to cart using Enterprise Cart API (Skill #2)
    window.cartAPI.addMultipleToCart(items, {
      openDrawer: false,
      dispatchEvents: true
    })
      .then((cart) => {
        // Update button to success state
        if (addAllButton) {
          addAllButton.innerHTML = '<span class="checkmark">✓</span>';
          addAllButton.classList.remove('loading');
          addAllButton.classList.add('success');

          // Reset button after animation
          setTimeout(() => {
            addAllButton.textContent = 'Add All to Cart';
            addAllButton.disabled = false;
            addAllButton.classList.remove('success');
            addAllButton.style.width = '';
            addAllButton.style.height = '';
          }, 1000);
        }

        // Close wishlist drawer
        this.toggleDrawer(false);

        // Open cart drawer after wishlist closes
        if (window.customCartDrawer) {
          setTimeout(() => {
            window.customCartDrawer.open();
          }, 300);
        }
      })
      .catch((error) => {
        window.debugLogger.error('Error adding all items to cart', error);

        // Reset button state on error
        if (addAllButton) {
          addAllButton.textContent = 'Add All to Cart';
          addAllButton.disabled = false;
          addAllButton.classList.remove('loading');
          addAllButton.style.width = '';
          addAllButton.style.height = '';
        }
      });
  }

  /**
   * Toggle wishlist item (add or remove)
   * CRITICAL: Called from all 4 wishlist button touchpoints
   * @param {Object} data - Product data from button dataset
   */
  toggleWishlistItem(data) {
    // Validate required data
    if (!data.productId || !data.variant || !data.productTitle) {
      window.debugLogger.error('Missing required product data');
      return;
    }

    // Get current wishlist items
    const wishlistItems = this.getWishlistItems();
    const existingItemIndex = wishlistItems.findIndex((item) => item.id === data.productId);

    if (existingItemIndex !== -1) {
      // Remove from wishlist
      wishlistItems.splice(existingItemIndex, 1);
    } else {
      // Add to wishlist
      wishlistItems.push({
        id: data.productId,
        variant: data.variant,
        variant_title: data.variantTitle || '',
        handle: data.productHandle,
        title: data.productTitle,
        image: data.productImage,
        price: data.productPrice ? `$${data.productPrice}` : '',
        compare_at_price: data.compareAtPrice
          ? String(data.compareAtPrice).replace(/[^0-9]/g, '')
          : '',
        quantity: 1,
        inventory_quantity: parseInt(data.inventoryQuantity, 10) || 9999
      });
    }

    // Save wishlist and update UI
    this.saveWishlist(wishlistItems);
    this.updateDrawer();
  }

  /**
   * Update wishlist buttons across the entire site
   * Updates button state (filled/empty heart) based on wishlist contents
   */
  updateWishlistButtons() {
    // Get the count of valid wishlist items
    const validItemCount = Array.isArray(this.wishlistItems)
      ? this.wishlistItems.filter((item) => item && typeof item === 'object' && item.id).length
      : 0;

    // Update buttons state
    document.querySelectorAll('.iWishAddColl').forEach((button) => {
      const productId = button.dataset.productId;
      if (productId) {
        button.classList.toggle(
          'iwishAdded',
          this.wishlistItems.some((item) => item && item.id === productId)
        );
      }
    });

    // Update header icon
    const headerIcon = document.querySelector('.header__icon--wishlist');
    if (headerIcon) {
      headerIcon.setAttribute('data-has-items', validItemCount > 0);
    }
  }

  /**
   * Update drawer content
   * Renders wishlist items or empty state
   */
  updateDrawer() {
    if (!this.drawer) return;

    const drawerBody = this.drawer.querySelector('#wishlist-drawer-body');
    if (!drawerBody) return;

    // First, clear drawer body content
    drawerBody.innerHTML = '';

    // Remove any existing login message or add all button
    document
      .querySelectorAll('.material-drawer__login-message, .material-drawer__add-all-to-cart')
      .forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });

    // Set the class based on wishlist state
    drawerBody.className = 'material-drawer__body';

    // Get valid item count
    const validItemCount = Array.isArray(this.wishlistItems)
      ? this.wishlistItems.filter((item) => item && typeof item === 'object' && item.id).length
      : 0;

    // Check if wishlist is empty
    const isWishlistEmpty = validItemCount === 0;

    // ONLY add the appropriate class - not both
    if (isWishlistEmpty) {
      // Empty wishlist - add empty class
      drawerBody.classList.add('wishlist-is-empty');

      // Empty state HTML - using inline SVG for crisp rendering at all resolutions
      const emptyHTML = `
        <div class="material-drawer__empty">
          <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Empty wishlist heart">
            <path d="M60 100C60 100 15 70 15 40C15 25 27 15 42 15C52 15 58 22 60 25C62 22 68 15 78 15C93 15 105 25 105 40C105 70 60 100 60 100Z" stroke="#CCCCCC" stroke-width="3" fill="none"/>
            <circle cx="45" cy="45" r="4" fill="#CCCCCC"/>
            <circle cx="75" cy="45" r="4" fill="#CCCCCC"/>
            <path d="M45 65C45 65 52 58 60 58C68 58 75 65 75 65" stroke="#CCCCCC" stroke-width="3" stroke-linecap="round" fill="none"/>
          </svg>
          <p class="empty-title">Your wishlist is empty!</p>
          <p class="empty-subtitle">Explore more and shortlist some items</p>
          <button class="material-drawer__continue">Continue Shopping</button>
        </div>
      `;
      drawerBody.innerHTML = emptyHTML;

      // Add event listener to continue shopping button
      const continueButton = drawerBody.querySelector('.material-drawer__continue');
      if (continueButton) {
        continueButton.addEventListener('click', () => this.toggleDrawer(false));
      }

      return;
    }

    // If we get here, we have items
    drawerBody.classList.add('wishlist-has-items');

    // Build content
    let drawerHTML = '';

    // Login message (if needed)
    if (!document.body.classList.contains('customer-logged-in')) {
      drawerHTML += `
        <div class="material-drawer__login-message">
          <p><a href="/account/login">Log in</a> or <a href="/account/register">Create an Account</a> To Save Your Wishlist.</p>
        </div>
      `;
    }

    // Start items container
    drawerHTML += '<div class="material-drawer__items">';

    // Add each item
    this.wishlistItems.forEach((item) => {
      if (!item.id || !item.title || !item.image) {
        window.debugLogger.error('Invalid wishlist item:', item);
        return;
      }

      // Calculate prices
      const priceNumber = parseFloat(String(item.price || '0').replace(/[^0-9.]/g, '')) || 0;
      let compareAtDollars = null;
      if (item.compare_at_price) {
        const compareAtCents = parseInt(String(item.compare_at_price).replace(/[^0-9]/g, ''), 10);
        if (!Number.isNaN(compareAtCents)) compareAtDollars = compareAtCents / 100;
      }
      let compPrice = null;
      if (compareAtDollars !== null && compareAtDollars > priceNumber) {
        compPrice = compareAtDollars.toFixed(2);
      } else {
        compPrice = (priceNumber * 1.3).toFixed(2);
      }

      drawerHTML += `
        <div class="material-drawer__item" data-product-id="${item.id}" data-variant-id="${item.variant}" data-compare-at-price="${item.compare_at_price || ''}">
          <div class="material-drawer__item-image-container">
            <a href="/products/${item.handle}" class="material-drawer__item-image-link">
              <img class="material-drawer__item-image" src="${item.image}" alt="${item.title}">
            </a>
          </div>
          <div class="material-drawer__item-info">
            <div class="material-drawer__item-details">
              <a href="/products/${item.handle}" class="material-drawer__item-title-link">
                <h3 class="material-drawer__item-title">${item.title}</h3>
              </a>
              <div class="material-drawer__item-price-container">
                <span class="material-drawer__item-price">${item.price}</span>
                ${parseFloat(compPrice) > priceNumber ? `<span class="material-drawer__item-comp-price">Comp. value $${compPrice}</span>` : ''}
              </div>
              <span class="material-drawer__item-variant">${item.variant_title || ''}</span>
              <div class="material-drawer__item-controls">
                <button class="material-drawer__add-to-cart">ADD TO BAG</button>
              </div>
            </div>
          </div>
          <button class="material-drawer__remove-button" aria-label="Remove item">
            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 4.5H14" stroke="var(--primary-color)" stroke-width="1.5" stroke-linecap="round"></path>
              <path d="M6 7V11" stroke="var(--primary-color)" stroke-width="1.5" stroke-linecap="round"></path>
              <path d="M10 7V11" stroke="var(--primary-color)" stroke-width="1.5" stroke-linecap="round"></path>
              <path d="M3 4.5L3.5 13.5C3.5 14.0523 3.94772 14.5 4.5 14.5H11.5C12.0523 14.5 12.5 14.0523 12.5 13.5L13 4.5" stroke="var(--primary-color)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
              <path d="M6.5 2.5H9.5C10.0523 2.5 10.5 2.94772 10.5 3.5V4.5H5.5V3.5C5.5 2.94772 5.94772 2.5 6.5 2.5Z" stroke="var(--primary-color)" stroke-width="1.5"></path>
            </svg>
          </button>
        </div>
      `;
    });

    // Close items container
    drawerHTML += '</div>';

    // Add the "Add All to Cart" button right after items
    drawerHTML += `
      <button class="material-drawer__add-all-to-cart">Add All to Bag</button>
    `;

    // Set HTML all at once
    drawerBody.innerHTML = drawerHTML;

    // Add event listener for the Add All to Cart button
    const addAllButton = drawerBody.querySelector('.material-drawer__add-all-to-cart');
    if (addAllButton) {
      addAllButton.addEventListener('click', () => this.addAllToCart());
    }
  }

  /**
   * Update cart count in header
   */
  // REMOVED: updateCartCount() - No longer needed!
  // Enterprise Cart API automatically handles cart icon updates via cart:updated events

  /**
   * Update item quantity in wishlist
   * @param {HTMLElement} item - Wishlist item element
   * @param {number} quantity - New quantity
   */
  updateItemQuantity(item, quantity) {
    const input = item.querySelector('.quantity-input');
    const maxQuantity = parseInt(item.dataset.inventoryQuantity, 10);
    const quantityMessage = item.querySelector('.quantity-message');

    // Create message element if it doesn't exist
    if (!quantityMessage) {
      const messageEl = document.createElement('div');
      messageEl.classList.add('quantity-message');
      messageEl.style.color = 'var(--color-error)';
      messageEl.style.fontSize = '12px';
      messageEl.style.marginTop = '5px';
      item.querySelector('.material-drawer__quantity')?.appendChild(messageEl);
    }

    if (quantity > maxQuantity) {
      quantity = maxQuantity;
      const msg = item.querySelector('.quantity-message');
      if (msg) msg.textContent = 'Maximum quantity reached';
      const increaseBtn = item.querySelector('.quantity-increase');
      if (increaseBtn) increaseBtn.disabled = true;
    } else {
      const msg = item.querySelector('.quantity-message');
      if (msg) msg.textContent = '';
      const increaseBtn = item.querySelector('.quantity-increase');
      if (increaseBtn) increaseBtn.disabled = false;
    }

    if (input) {
      input.value = quantity;
      // Update the item's data
      const itemId = item.dataset.productId;
      const wishlistItems = this.getWishlistItems();
      const wishlistItem = wishlistItems.find((i) => i.id === itemId);
      if (wishlistItem) {
        wishlistItem.quantity = quantity;
        this.saveWishlist(wishlistItems);
      }
    }
  }

  /**
   * Set up drawer events (called after drawer content is updated)
   */
  setupDrawerEvents() {
    if (!this.drawer) return;

    const drawerBody = this.drawer.querySelector('#wishlist-drawer-body');
    if (!drawerBody) return;

    // Remove any existing event listeners (for safety)
    const newDrawerBody = drawerBody.cloneNode(true);
    drawerBody.parentNode.replaceChild(newDrawerBody, drawerBody);

    // Add click event delegation
    newDrawerBody.addEventListener('click', (event) => {
      // IMPORTANT: Stop propagation for all clicks inside drawer
      // This prevents clicks from bubbling up to the outside click handler
      event.stopPropagation();

      // Continue shopping button
      if (event.target.classList.contains('material-drawer__continue')) {
        this.toggleDrawer(false);
      }

      // Quantity increase button
      else if (event.target.classList.contains('quantity-increase')) {
        const input = event.target
          .closest('.material-drawer__quantity')
          .querySelector('.quantity-input');
        if (input) {
          const currentValue = parseInt(input.value, 10) || 1;
          input.value = currentValue + 1;
          this.updateItemQuantity(event.target.closest('.material-drawer__item'), currentValue + 1);
        }
      }

      // Quantity decrease button
      else if (event.target.classList.contains('quantity-decrease')) {
        const input = event.target
          .closest('.material-drawer__quantity')
          .querySelector('.quantity-input');
        if (input) {
          const currentValue = parseInt(input.value, 10) || 1;
          if (currentValue > 1) {
            input.value = currentValue - 1;
            this.updateItemQuantity(
              event.target.closest('.material-drawer__item'),
              currentValue - 1
            );
          }
        }
      }

      // Remove item
      else if (
        event.target.classList.contains('trash-btn') ||
        event.target.closest('.material-drawer__remove-button')
      ) {
        const item = event.target.closest('.material-drawer__item');
        if (item) {
          this.removeFromWishlist(item);
        }
      }

      // Add to Bag
      else if (event.target.classList.contains('material-drawer__add-to-cart')) {
        const item = event.target.closest('.material-drawer__item');
        if (item) {
          this.addToCart(item);
        }
      }

      // Add all to cart
      else if (event.target.classList.contains('material-drawer__add-all-to-cart')) {
        this.addAllToCart();
      }
    });

    // Add change event for quantity input
    newDrawerBody.addEventListener('change', (event) => {
      if (event.target.classList.contains('quantity-input')) {
        const item = event.target.closest('.material-drawer__item');
        if (item) {
          let value = parseInt(event.target.value, 10);
          if (isNaN(value) || value < 1) {
            value = 1;
            event.target.value = value;
          }
          this.updateItemQuantity(item, value);
        }
      }
    });
  }

  /**
   * Load wishlist from localStorage
   */
  loadWishlist() {
    try {
      const storedItems = this.getWishlistItems();

      // Filter out any items that don't have required properties
      this.wishlistItems = storedItems.filter((item) => {
        return item && item.id && typeof item === 'object';
      });

      // If we filtered items, update localStorage with clean data
      if (storedItems.length !== this.wishlistItems.length) {
        window.debugLogger.warn('Found and removed invalid wishlist items');
        this.saveWishlist(this.wishlistItems);
      }
    } catch (error) {
      window.debugLogger.error('Error loading wishlist:', error);
      this.wishlistItems = [];
      this.saveWishlist([]);
    }
  }

  /**
   * Clean up wishlist data (remove invalid items)
   */
  cleanupWishlist() {
    if (Array.isArray(this.wishlistItems)) {
      const originalCount = this.wishlistItems.length;

      // Only keep items with required fields
      this.wishlistItems = this.wishlistItems.filter((item) => {
        return item && typeof item === 'object' && item.id && item.title && item.image;
      });

      // If we removed any items, update localStorage
      if (originalCount !== this.wishlistItems.length) {
        window.debugLogger.warn(
          `Removed ${originalCount - this.wishlistItems.length} invalid wishlist items`
        );
        localStorage.setItem('wishlist', JSON.stringify(this.wishlistItems));
      }
    } else {
      window.debugLogger.error('Wishlist data was corrupted (not an array) - resetting');
      this.wishlistItems = [];
      localStorage.setItem('wishlist', JSON.stringify([]));
    }
  }

  /**
   * Get wishlist items from localStorage
   * @returns {Array} Wishlist items
   */
  getWishlistItems() {
    try {
      const items = JSON.parse(localStorage.getItem('wishlist')) || [];
      return Array.isArray(items) ? items : [];
    } catch (error) {
      window.debugLogger.error('Error getting wishlist items:', error);
      return [];
    }
  }

  /**
   * Save wishlist items to localStorage
   * @param {Array} items - Wishlist items to save
   */
  saveWishlist(items) {
    try {
      localStorage.setItem('wishlist', JSON.stringify(items));
      this.wishlistItems = items;
      this.updateWishlistButtons();
    } catch (error) {
      window.debugLogger.error('Error saving wishlist:', error);
    }
  }
}

// Define custom element
customElements.define('wishlist-drawer', EnterpriseWishlistDrawer);
